import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * A click-only date + time picker. Renders a compact "Pick date · Pick time"
 * trigger button; opens a popover with a month calendar + hour/minute/AM-PM
 * columns. No typing is possible.
 *
 * Props:
 *   date, time      — strings (yyyy-mm-dd, HH:MM in 24h)
 *   onChange({date, time})
 *   testIdPrefix    — used to derive data-testid attrs
 *   label           — accessible label shown in the button when unset
 */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const pad2 = (n) => String(n).padStart(2, "0");

const to12h = (h24) => {
  const h = parseInt(h24, 10);
  if (Number.isNaN(h)) return { h12: 12, ampm: "AM" };
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { h12, ampm };
};

const to24h = (h12, ampm) => {
  const h = parseInt(h12, 10) % 12;
  return ampm === "PM" ? h + 12 : h;
};

const buildMonth = (year, month /* 0-11 */) => {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const parseDate = (isoDate) => {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const DateTimePicker = ({
  date = "",
  time = "",
  onChange,
  testIdPrefix = "dtp",
  label = "Pick date & time",
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const now = new Date();
  const selectedDate = parseDate(date) || null;
  const [viewYear, setViewYear] = useState(
    selectedDate ? selectedDate.getFullYear() : now.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate ? selectedDate.getMonth() : now.getMonth()
  );

  // Whenever a selected date arrives from outside, jump the view to it.
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const cells = useMemo(() => buildMonth(viewYear, viewMonth), [viewYear, viewMonth]);

  const { h12: currentH12, ampm: currentAmPm } = time ? to12h(time.slice(0, 2)) : { h12: 12, ampm: "AM" };
  const currentMinute = time ? parseInt(time.slice(3, 5), 10) || 0 : 0;

  const emit = (nextDate, nextTime) => onChange?.({ date: nextDate, time: nextTime });

  const pickDay = (d) => {
    const iso = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(d)}`;
    emit(iso, time || "12:00");
  };

  const pickHour = (h12) => {
    const h24 = to24h(h12, currentAmPm);
    emit(date, `${pad2(h24)}:${pad2(currentMinute)}`);
  };

  const pickMinute = (m) => {
    const h24 = to24h(currentH12, currentAmPm);
    emit(date, `${pad2(h24)}:${pad2(m)}`);
  };

  const pickAmPm = (ap) => {
    const h24 = to24h(currentH12, ap);
    emit(date, `${pad2(h24)}:${pad2(currentMinute)}`);
  };

  const summary = (() => {
    if (!date && !time) return label;
    const d = parseDate(date);
    const dateStr = d
      ? d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
      : "Pick a date";
    const timeStr = time
      ? `${currentH12}:${pad2(currentMinute)} ${currentAmPm}`
      : "Pick a time";
    return `${dateStr} · ${timeStr}`;
  })();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isToday = (d) => {
    return (
      viewYear === now.getFullYear() &&
      viewMonth === now.getMonth() &&
      d === now.getDate()
    );
  };
  const isSelected = (d) =>
    selectedDate &&
    viewYear === selectedDate.getFullYear() &&
    viewMonth === selectedDate.getMonth() &&
    d === selectedDate.getDate();

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  // Quarter-hour steps keep the list short + no typing needed.
  const minutes = [0, 15, 30, 45];

  return (
    <div className="dtp" ref={wrapRef}>
      <button
        type="button"
        className={`dtp-trigger${date && time ? " has-value" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`${testIdPrefix}-trigger`}
      >
        <CalendarIcon size={14} />
        <span className="dtp-trigger-text">{summary}</span>
      </button>

      {open && (
        <div className="dtp-popover" role="dialog" data-testid={`${testIdPrefix}-popover`}>
          <div className="dtp-cal">
            <div className="dtp-cal-header">
              <button type="button" className="dtp-nav" onClick={prevMonth} aria-label="Previous month" data-testid={`${testIdPrefix}-prev`}>
                <ChevronLeft size={14} />
              </button>
              <div className="dtp-cal-title">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </div>
              <button type="button" className="dtp-nav" onClick={nextMonth} aria-label="Next month" data-testid={`${testIdPrefix}-next`}>
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="dtp-cal-grid dtp-cal-daynames">
              {DAY_LABELS.map((d, i) => (
                <div key={i} className="dtp-daylabel">{d}</div>
              ))}
            </div>
            <div className="dtp-cal-grid">
              {cells.map((d, i) =>
                d === null ? (
                  <div key={i} className="dtp-day dtp-day-blank" />
                ) : (
                  <button
                    type="button"
                    key={i}
                    className={`dtp-day${isSelected(d) ? " selected" : ""}${isToday(d) ? " today" : ""}`}
                    onClick={() => pickDay(d)}
                    data-testid={`${testIdPrefix}-day-${d}`}
                  >
                    {d}
                  </button>
                )
              )}
            </div>
          </div>

          <div className="dtp-time">
            <div className="dtp-time-header">
              <Clock size={14} /> Time
            </div>
            <div className="dtp-time-cols">
              <div className="dtp-col" role="listbox" aria-label="Hour">
                {hours.map((h) => (
                  <button
                    type="button"
                    key={h}
                    className={`dtp-chip${currentH12 === h && !!time ? " active" : ""}`}
                    onClick={() => pickHour(h)}
                    data-testid={`${testIdPrefix}-hour-${h}`}
                  >
                    {h}
                  </button>
                ))}
              </div>
              <div className="dtp-col" role="listbox" aria-label="Minute">
                {minutes.map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`dtp-chip${currentMinute === m && !!time ? " active" : ""}`}
                    onClick={() => pickMinute(m)}
                    data-testid={`${testIdPrefix}-minute-${m}`}
                  >
                    :{pad2(m)}
                  </button>
                ))}
              </div>
              <div className="dtp-col dtp-col-ampm" role="listbox" aria-label="AM or PM">
                {["AM", "PM"].map((ap) => (
                  <button
                    type="button"
                    key={ap}
                    className={`dtp-chip${currentAmPm === ap && !!time ? " active" : ""}`}
                    onClick={() => pickAmPm(ap)}
                    data-testid={`${testIdPrefix}-ampm-${ap}`}
                  >
                    {ap}
                  </button>
                ))}
              </div>
            </div>
            <div className="dtp-done-row">
              <button
                type="button"
                className="btn-solid"
                onClick={() => setOpen(false)}
                data-testid={`${testIdPrefix}-done`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
